import { useEffect, useState } from 'react'
import {
  Box,
  SimpleGrid,
  Heading,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Button,
  useToast,
  Center,
  Card,
  CardHeader,
  CardBody,
  Stack,
  Badge,
  IconButton,
  ButtonGroup,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Flex,
  HStack,
  Divider,
  useColorModeValue,
  Skeleton,
  SkeletonText,
  Icon,
  Tooltip,
} from '@chakra-ui/react'
import { FiMinus, FiPlus, FiAlertCircle, FiInfo, FiServer, FiRefreshCw } from 'react-icons/fi'
import { useAuth } from '../context/AuthContext'

interface Dyno {
  type: string
  size: string
  quantity: number
  state: string
}

interface App {
  name: string
  id: string
  dynos: Dyno[]
}

interface ScaleConfirmation {
  app: string
  dynoType: string
  currentCount: number
  newCount: number
}

const Dashboard = () => {
  const [apps, setApps] = useState<App[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isScaling, setIsScaling] = useState(false)
  const [scaleConfirmation, setScaleConfirmation] = useState<ScaleConfirmation | null>(null)
  const { apiKey } = useAuth()
  const toast = useToast()
  const { isOpen, onOpen, onClose } = useDisclosure()
  
  const cardBg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const textColor = useColorModeValue('gray.800', 'gray.100')
  const mutedTextColor = useColorModeValue('gray.600', 'gray.400')
  const statBg = useColorModeValue('gray.50', 'gray.700')

  const fetchApps = async () => {
    try {
      // Fetch apps
      const appsResponse = await fetch('https://api.heroku.com/apps', {
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Accept': 'application/vnd.heroku+json; version=3',
        },
      })

      if (!appsResponse.ok) throw new Error('Failed to fetch apps')
      const appsData = await appsResponse.json()

      // Fetch formation info for each app (this includes all possible dyno types)
      const appsWithDynos = await Promise.all(
        appsData.map(async (app: any) => {
          const [dynosResponse, formationResponse] = await Promise.all([
            fetch(`https://api.heroku.com/apps/${app.name}/dynos`, {
              headers: {
                'Authorization': `Bearer ${apiKey}`,
                'Accept': 'application/vnd.heroku+json; version=3',
              },
            }),
            fetch(`https://api.heroku.com/apps/${app.name}/formation`, {
              headers: {
                'Authorization': `Bearer ${apiKey}`,
                'Accept': 'application/vnd.heroku+json; version=3',
              },
            })
          ])

          let dynos = []
          let formation = []

          if (dynosResponse.ok) {
            dynos = await dynosResponse.json()
          }

          if (formationResponse.ok) {
            formation = await formationResponse.json()
          }

          // Create a map of dyno types to their quantities and states
          const dynoMap = formation.reduce((acc: any, form: any) => {
            acc[form.type] = {
              quantity: form.quantity,
              size: form.size,
              type: form.type,
              state: 'unknown',
            }
            return acc
          }, {})

          // Update the states of the dynos
          dynos.forEach((dyno: any) => {
            if (dynoMap[dyno.type]) {
              dynoMap[dyno.type].state = dyno.state
            }
          })

          return {
            name: app.name,
            id: app.id,
            dynos: Object.values(dynoMap),
          }
        })
      )

      setApps(appsWithDynos)
    } catch (error) {
      console.error('Error fetching apps:', error)
      toast({
        title: 'Error fetching apps',
        description: 'There was an error fetching your Heroku apps. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleScale = async (app: string, dynoType: string, currentCount: number, delta: number) => {
    const newCount = Math.max(0, currentCount + delta)
    
    // If scaling to 0, show confirmation
    if (newCount === 0) {
      setScaleConfirmation({
        app,
        dynoType,
        currentCount,
        newCount,
      })
      onOpen()
      return
    }
    
    await scaleApp(app, dynoType, newCount)
  }

  const confirmScale = async () => {
    if (!scaleConfirmation) return
    
    const { app, dynoType, newCount } = scaleConfirmation
    await scaleApp(app, dynoType, newCount)
    onClose()
  }

  const scaleApp = async (app: string, dynoType: string, quantity: number) => {
    setIsScaling(true)
    try {
      const response = await fetch(`https://api.heroku.com/apps/${app}/formation/${dynoType}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Accept': 'application/vnd.heroku+json; version=3',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          quantity,
        }),
      })

      if (!response.ok) throw new Error('Failed to scale app')

      toast({
        title: 'Dyno scaled successfully',
        description: `${app} ${dynoType} dynos scaled to ${quantity}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      })

      // Refresh the app list
      fetchApps()
    } catch (error) {
      console.error('Error scaling app:', error)
      toast({
        title: 'Error scaling app',
        description: 'There was an error scaling your app. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsScaling(false)
    }
  }

  useEffect(() => {
    fetchApps()
  }, [apiKey])

  const getDynoStateColor = (state: string) => {
    switch (state.toLowerCase()) {
      case 'up':
        return 'green'
      case 'starting':
        return 'yellow'
      case 'down':
        return 'red'
      case 'crashed':
        return 'red'
      case 'idle':
        return 'blue'
      default:
        return 'gray'
    }
  }

  const renderAppCards = () => {
    if (isLoading) {
      return Array(3).fill(0).map((_, i) => (
        <Card key={i} bg={cardBg} boxShadow="md" borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
          <CardHeader pb={2}>
            <Skeleton height="24px" width="70%" mb={2} />
            <SkeletonText noOfLines={1} width="40%" />
          </CardHeader>
          <CardBody pt={2}>
            <Stack spacing={4}>
              {Array(2).fill(0).map((_, j) => (
                <Box key={j} p={4} borderRadius="md" bg={statBg}>
                  <Skeleton height="20px" width="50%" mb={2} />
                  <SkeletonText noOfLines={2} spacing={2} />
                </Box>
              ))}
            </Stack>
          </CardBody>
        </Card>
      ))
    }

    if (apps.length === 0) {
      return (
        <Card bg={cardBg} boxShadow="md" borderRadius="lg" borderWidth="1px" borderColor={borderColor} p={6}>
          <Center flexDirection="column" py={10}>
            <Icon as={FiInfo} boxSize={12} color="gray.400" mb={4} />
            <Heading size="md" mb={2} textAlign="center">No apps found</Heading>
            <Text color={mutedTextColor} textAlign="center">
              No Heroku apps were found for your account.
            </Text>
            <Button 
              mt={6} 
              leftIcon={<FiRefreshCw />} 
              onClick={fetchApps} 
              isLoading={isLoading}
            >
              Refresh
            </Button>
          </Center>
        </Card>
      )
    }

    return apps.map((app) => (
      <Card 
        key={app.id} 
        bg={cardBg} 
        boxShadow="md" 
        borderRadius="lg" 
        borderWidth="1px" 
        borderColor={borderColor}
        transition="all 0.2s"
        _hover={{ boxShadow: 'lg', transform: 'translateY(-2px)' }}
      >
        <CardHeader pb={2}>
          <Flex justifyContent="space-between" alignItems="center">
            <Heading size="md" color={textColor}>{app.name}</Heading>
            <Tooltip label="Refresh app data">
              <IconButton
                aria-label="Refresh app data"
                icon={<FiRefreshCw />}
                size="sm"
                variant="ghost"
                onClick={() => fetchApps()}
                isLoading={isLoading}
              />
            </Tooltip>
          </Flex>
          <Text fontSize="sm" color={mutedTextColor} mt={1}>
            {app.dynos.length} dyno type{app.dynos.length !== 1 ? 's' : ''}
          </Text>
        </CardHeader>
        <Divider borderColor={borderColor} />
        <CardBody pt={4}>
          <Stack spacing={4}>
            {app.dynos.map((dyno) => (
              <Box 
                key={dyno.type} 
                p={4} 
                borderRadius="md" 
                bg={statBg}
                borderWidth="1px"
                borderColor={borderColor}
              >
                <Flex justifyContent="space-between" alignItems="center" mb={2}>
                  <HStack>
                    <Icon as={FiServer} color={mutedTextColor} />
                    <Text fontWeight="medium" color={textColor}>{dyno.type}</Text>
                    <Badge colorScheme={getDynoStateColor(dyno.state)} ml={2}>
                      {dyno.state}
                    </Badge>
                  </HStack>
                  <Text fontSize="sm" color={mutedTextColor}>{dyno.size}</Text>
                </Flex>
                
                <Flex justifyContent="space-between" alignItems="center" mt={3}>
                  <Stat>
                    <StatLabel color={mutedTextColor}>Current Count</StatLabel>
                    <StatNumber>{dyno.quantity}</StatNumber>
                    <StatHelpText>
                      {dyno.quantity === 0 ? 'Scaled to zero' : `${dyno.quantity} dyno${dyno.quantity !== 1 ? 's' : ''} running`}
                    </StatHelpText>
                  </Stat>
                  
                  <ButtonGroup size="sm" isAttached variant="outline">
                    <IconButton
                      aria-label={`Decrease ${dyno.type} count`}
                      icon={<FiMinus />}
                      onClick={() => handleScale(app.name, dyno.type, dyno.quantity, -1)}
                      isDisabled={dyno.quantity === 0 || isScaling}
                      colorScheme={dyno.quantity > 0 ? "red" : undefined}
                    />
                    <IconButton
                      aria-label={`Increase ${dyno.type} count`}
                      icon={<FiPlus />}
                      onClick={() => handleScale(app.name, dyno.type, dyno.quantity, 1)}
                      isDisabled={isScaling}
                      colorScheme="green"
                    />
                  </ButtonGroup>
                </Flex>
              </Box>
            ))}
          </Stack>
        </CardBody>
      </Card>
    ))
  }

  return (
    <Box>
      <Flex justifyContent="space-between" alignItems="center" mb={6}>
        <Box>
          <Heading size="lg" mb={2} color={textColor}>Dashboard</Heading>
          <Text color={mutedTextColor}>
            Manage your Heroku dynos across all applications
          </Text>
        </Box>
        <Button 
          leftIcon={<FiRefreshCw />} 
          onClick={fetchApps} 
          isLoading={isLoading}
          variant="outline"
        >
          Refresh
        </Button>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
        {renderAppCards()}
      </SimpleGrid>

      {/* Confirmation Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Scale to Zero</ModalHeader>
          <ModalBody>
            <HStack spacing={2} mb={4}>
              <Icon as={FiAlertCircle} color="orange.500" boxSize={5} />
              <Text fontWeight="medium">
                Are you sure you want to scale {scaleConfirmation?.app} {scaleConfirmation?.dynoType} to 0?
              </Text>
            </HStack>
            <Text color={mutedTextColor}>
              This will stop all dynos of this type and may affect your application's availability.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="red" onClick={confirmScale} isLoading={isScaling}>
              Scale to Zero
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}

export default Dashboard 